/* global theme */

if (!customElements.get('shipping-estimate')) {
  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      // The element is recreated whenever the cart summary re-renders, so guard
      // against double initialisation within the same instance.
      if (this.initialised) return;
      this.initialised = true;

      this.country = this.dataset.country || 'CH';
      this.province = this.dataset.province || '';
      this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      this.showTotal = this.dataset.showTotal === 'true';
      this.updateCheckout = this.dataset.updateCheckout === 'true';
      this.freeLabel = this.dataset.labelFree || '';
      this.fromLabel = this.dataset.labelFrom || '';

      this.valueEl = this.querySelector('.js-shipping-estimate-value');
      this.totalRow = this.querySelector('.js-shipping-estimate-total');
      this.totalValueEl = this.querySelector('.js-shipping-estimate-total-value');

      this.loadRates();
    }

    /**
     * Cache key tied to the current cart state, so identical states (e.g. the
     * drawer being re-opened) are served from cache instead of re-fetching.
     * @returns {string}
     */
    get cacheKey() {
      return `shipping-estimate:${this.country}:${this.cartTotal}`;
    }

    /**
     * Fetches live Shopify shipping rates for the default destination and renders
     * the cheapest one. Fails silently, leaving the standard checkout notice.
     */
    async loadRates() {
      const cached = this.readCache();
      if (cached) {
        this.render(cached);
        return;
      }

      const params = new URLSearchParams();
      params.append('shipping_address[country]', this.country);
      if (this.province) params.append('shipping_address[province]', this.province);

      try {
        const response = await fetch(`${theme.routes.cart}/shipping_rates.json?${params.toString()}`);
        if (!response.ok) return;

        const data = await response.json();
        const rates = (data && data.shipping_rates) || [];
        if (!rates.length) return;

        const prices = rates
          .map((rate) => parseFloat(rate.price))
          .filter((price) => !Number.isNaN(price));
        if (!prices.length) return;

        const lowest = Math.min(...prices);
        const result = { lowest, multiple: Math.max(...prices) !== lowest };

        this.writeCache(result);
        this.render(result);
      } catch (error) {
        // Fail silently - the existing "calculated at checkout" notice remains.
      }
    }

    /**
     * Reads a cached rate result for the current cart state.
     * @returns {object|null}
     */
    readCache() {
      try {
        const raw = sessionStorage.getItem(this.cacheKey);
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    }

    /**
     * Stores a rate result for the current cart state.
     * @param {object} result - Rate result to cache.
     */
    writeCache(result) {
      try {
        sessionStorage.setItem(this.cacheKey, JSON.stringify(result));
      } catch (error) {
        // Ignore (private mode / quota exceeded).
      }
    }

    /**
     * Formats an amount (in major currency units) using the shop money format and
     * returns plain text. Prefers the format without the currency code (to match the
     * line-item prices in the cart) and strips any markup the format may contain
     * (e.g. a currency-app span), so it can be rendered safely via textContent.
     * @param {number} amount - Amount in major units (e.g. 7.9).
     * @returns {string}
     */
    formatMoney(amount) {
      const settings = theme.settings || {};
      const format = settings.moneyFormat || settings.moneyWithCurrencyFormat || '{{ amount }}';
      return format
        .replace(/\{\{\s*\w+\s*\}\}/, amount.toFixed(2))
        .replace(/<[^>]*>/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    }

    /**
     * Calculates the estimated total incl. shipping in major currency units.
     * @param {object} result - Rate result ({ lowest }).
     * @returns {number}
     */
    getEstimatedTotal(result) {
      return (this.cartTotal / 100) + Math.max(result.lowest, 0);
    }

    /**
     * Updates the checkout button total within the nearest cart summary.
     * @param {object} result - Rate result ({ lowest }).
     */
    updateCheckoutTotal(result) {
      if (!this.updateCheckout) return;

      const container = this.closest('.cart-drawer__summary, .cart__summary, cart-drawer');
      if (!container) return;

      const formatted = this.formatMoney(this.getEstimatedTotal(result));
      container.querySelectorAll('.js-checkout-total').forEach((el) => {
        el.textContent = formatted;
      });
    }

    /**
     * Renders the estimate and reveals the element.
     * @param {object} result - Rate result ({ lowest, multiple }).
     */
    render(result) {
      if (!this.valueEl) return;

      if (result.lowest <= 0) {
        this.valueEl.textContent = this.freeLabel || this.formatMoney(0);
        this.classList.add('shipping-estimate--free');
      } else {
        const price = this.formatMoney(result.lowest);
        this.valueEl.textContent = result.multiple && this.fromLabel
          ? `${this.fromLabel} ${price}`
          : price;
      }

      const total = this.getEstimatedTotal(result);

      if (this.showTotal && this.totalRow && this.totalValueEl) {
        this.totalValueEl.textContent = this.formatMoney(total);
        this.totalRow.hidden = false;
      }

      this.updateCheckoutTotal(result);
      this.hidden = false;
    }
  }

  customElements.define('shipping-estimate', ShippingEstimate);
}
